import React, { useMemo, useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { ChevronDown, ChevronRight } from 'lucide-react';

// Renders the session_summary map from an igloo_exited event:
// column-oriented per-snapshot arrays (timestampMs + one array per metric key).
//
// One row per metric, grouped by dotted-prefix category.
// Categories are collapsed by default; expand/collapse all in the toolbar.
//
// Numeric metrics: a step-line chart showing the discrete sampled values
// (no interpolation — value held until next sample). Categorical metrics: a
// chip swimlane with the value at each sample, coloured by value so repeats
// are visible at a glance.

function isNumeric(v) {
  return typeof v === 'number' && Number.isFinite(v);
}

function categorise(keys) {
  const groups = {};
  for (const k of keys) {
    const dot = k.indexOf('.');
    const group = dot < 0 ? '_root' : k.substring(0, dot);
    if (!groups[group]) groups[group] = [];
    groups[group].push(k);
  }
  for (const g of Object.keys(groups)) groups[g].sort();
  return groups;
}

function summariseNumeric(values) {
  const nums = values.filter(isNumeric);
  if (nums.length === 0) return null;
  let min = Infinity;
  let max = -Infinity;
  let sum = 0;
  for (const v of nums) {
    if (v < min) min = v;
    if (v > max) max = v;
    sum += v;
  }
  return { count: nums.length, min, max, mean: sum / nums.length };
}

function summariseCategorical(values) {
  const nonNull = values.filter((v) => v !== null && v !== undefined);
  const distinct = [...new Set(nonNull.map((v) => String(v)))];
  return {
    count: nonNull.length,
    distinct,
    last: nonNull.length > 0 ? nonNull[nonNull.length - 1] : null,
  };
}

function formatNum(n) {
  if (!Number.isFinite(n)) return '?';
  if (Math.abs(n) >= 10000) return n.toExponential(1);
  if (Number.isInteger(n)) return String(n);
  if (Math.abs(n) >= 100) return n.toFixed(0);
  if (Math.abs(n) >= 10) return n.toFixed(1);
  return n.toFixed(2);
}

// Pastel palette for categorical chips, indexed by string hash so the same
// value always lands on the same colour and runs of identical values are
// visually contiguous.
const CHIP_PALETTE = [
  'rgb(243 244 246)', // gray-100
  'rgb(254 243 199)', // amber-100
  'rgb(220 252 231)', // green-100
  'rgb(219 234 254)', // blue-100
  'rgb(243 232 255)', // purple-100
  'rgb(254 226 226)', // red-100
  'rgb(207 250 254)', // cyan-100
  'rgb(255 237 213)', // orange-100
];

function chipColor(value) {
  if (value === null || value === undefined) return 'transparent';
  if (value === true) return 'rgb(187 247 208)'; // green-200
  if (value === false) return 'rgb(254 202 202)'; // red-200
  let h = 0;
  const s = String(value);
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return CHIP_PALETTE[h % CHIP_PALETTE.length];
}

function chipText(value) {
  if (value === null || value === undefined) return '—';
  if (value === true) return '✓';
  if (value === false) return '✗';
  return String(value);
}

function formatT(ms, baseMs) {
  return Math.round((ms - baseMs) / 1000);
}

function NumericRow({ displayKey, fullKey, values, timestamps, baseMs }) {
  const stats = summariseNumeric(values);
  const data = useMemo(
    () =>
      timestamps.map((t, i) => ({
        tSec: formatT(t, baseMs),
        value: isNumeric(values[i]) ? values[i] : null,
      })),
    [timestamps, values, baseMs]
  );
  const sampleCount = values.filter((v) => v !== null && v !== undefined).length;

  return (
    <div className="border rounded-md p-3 bg-card">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
        <div className="font-mono text-sm" title={fullKey}>{displayKey}</div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground tabular-nums">
          {stats ? (
            <>
              <span>min <span className="text-foreground">{formatNum(stats.min)}</span></span>
              <span>mean <span className="text-foreground">{formatNum(stats.mean)}</span></span>
              <span>max <span className="text-foreground">{formatNum(stats.max)}</span></span>
            </>
          ) : (
            <span>no numeric values</span>
          )}
          <span>· {sampleCount} samples</span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={70}>
        <LineChart data={data} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
          <XAxis
            dataKey="tSec"
            type="number"
            domain={['dataMin', 'dataMax']}
            tick={{ fontSize: 10 }}
            stroke="#9ca3af"
            tickFormatter={(v) => `${v}s`}
          />
          <YAxis
            tick={{ fontSize: 10 }}
            stroke="#9ca3af"
            domain={['auto', 'auto']}
            width={42}
          />
          <Tooltip
            labelFormatter={(v) => `t=${v}s`}
            formatter={(v) => [formatNum(v), 'value']}
            contentStyle={{ fontSize: 11 }}
          />
          {stats && stats.min !== stats.max && (
            <ReferenceLine y={stats.mean} stroke="#a78bfa" strokeDasharray="3 3" strokeOpacity={0.5} />
          )}
          <Line
            type="stepAfter"
            dataKey="value"
            stroke="#7c3aed"
            strokeWidth={1.5}
            dot={{ r: 2.5, fill: '#7c3aed' }}
            connectNulls={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function CategoricalRow({ displayKey, fullKey, values, timestamps, baseMs }) {
  const s = summariseCategorical(values);

  return (
    <div className="border rounded-md p-3 bg-card">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-2">
        <div className="font-mono text-sm" title={fullKey}>{displayKey}</div>
        <div className="text-xs text-muted-foreground">
          {s.count} samples · {s.distinct.length} distinct · last <span className="text-foreground font-mono">{chipText(s.last)}</span>
        </div>
      </div>
      <div className="flex gap-px h-7 rounded overflow-hidden border">
        {values.map((v, i) => {
          const tSec = formatT(timestamps[i], baseMs);
          const isMissing = v === null || v === undefined;
          return (
            <div
              key={i}
              className={`flex-1 min-w-0 flex items-center justify-center text-[10px] font-mono px-1 ${
                isMissing ? 'bg-muted/30 border-r border-dashed border-muted-foreground/30' : ''
              }`}
              style={{ background: !isMissing ? chipColor(v) : undefined }}
              title={`t=${tSec}s · ${isMissing ? '(missing)' : String(v)}`}
            >
              <span className="truncate">{chipText(v)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MetricGroup({ group, keys, summary, baseMs, expanded, onToggle }) {
  const timestamps = summary.timestampMs || [];

  return (
    <div className="border rounded-md">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/50 text-left"
      >
        {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <span className="font-medium text-sm">{group}</span>
        <span className="text-xs text-muted-foreground">{keys.length} metric{keys.length === 1 ? '' : 's'}</span>
      </button>
      {expanded && (
        <div className="p-3 space-y-2 border-t">
          {keys.map((k) => {
            const values = summary[k] || [];
            const numeric = values.some(isNumeric);
            const displayKey = k.startsWith(group + '.') ? k.substring(group.length + 1) : k;
            return numeric ? (
              <NumericRow
                key={k}
                displayKey={displayKey}
                fullKey={k}
                values={values}
                timestamps={timestamps}
                baseMs={baseMs}
              />
            ) : (
              <CategoricalRow
                key={k}
                displayKey={displayKey}
                fullKey={k}
                values={values}
                timestamps={timestamps}
                baseMs={baseMs}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function SessionStatsView({ summary }) {
  const [filter, setFilter] = useState('');
  const [expanded, setExpanded] = useState(() => new Set());

  const allKeys = useMemo(
    () => (summary ? Object.keys(summary).filter((k) => k !== 'timestampMs') : []),
    [summary]
  );

  const filteredKeys = useMemo(() => {
    if (!filter.trim()) return allKeys;
    const f = filter.trim().toLowerCase();
    return allKeys.filter((k) => k.toLowerCase().includes(f));
  }, [allKeys, filter]);

  const groups = useMemo(() => categorise(filteredKeys), [filteredKeys]);
  const groupNames = useMemo(() => Object.keys(groups).sort(), [groups]);
  const baseMs = summary?.timestampMs?.[0] || 0;

  // Auto-expand groups whose name or any key matches an active filter so
  // the user can see what they're searching for without an extra click.
  useEffect(() => {
    if (!filter.trim()) return;
    setExpanded(new Set(groupNames));
  }, [filter, groupNames]);

  const toggle = (group) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };
  const expandAll = () => setExpanded(new Set(groupNames));
  const collapseAll = () => setExpanded(new Set());

  if (!summary || allKeys.length === 0) {
    return (
      <div className="text-sm text-muted-foreground py-6 text-center">
        No session_summary on this exit event.
      </div>
    );
  }

  const sampleCount = summary.timestampMs?.length || 0;
  const span = sampleCount > 1
    ? Math.round((summary.timestampMs[sampleCount - 1] - summary.timestampMs[0]) / 1000)
    : 0;
  const interval = sampleCount > 1 ? Math.round(span / (sampleCount - 1)) : 0;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          placeholder="Filter metrics (e.g. camera, network, fps)…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="max-w-sm h-8 text-sm"
        />
        <div className="text-xs text-muted-foreground flex-1 min-w-fit">
          {sampleCount} samples · ~{interval}s interval · {filteredKeys.length}/{allKeys.length} metrics
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={expandAll}>
            Expand all
          </Button>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={collapseAll}>
            Collapse all
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        {groupNames.map((g) => (
          <MetricGroup
            key={g}
            group={g}
            keys={groups[g]}
            summary={summary}
            baseMs={baseMs}
            expanded={expanded.has(g)}
            onToggle={() => toggle(g)}
          />
        ))}
      </div>
    </div>
  );
}

export default SessionStatsView;
